import React, { Fragment, useState, useEffect } from 'react'
import './DocumentHeader.scss'
import {
  FolderAddOutlined,
  FileAddOutlined,
  RightOutlined, FileTwoTone
} from '@ant-design/icons'
import { Button, Dropdown, Input, message, Modal } from 'antd'
import {
  FaFileWord,
  FaFileExcel,
  FaFilePowerpoint,
  FaDesktop,
  FaDownload,
  FaExclamationTriangle
} from 'react-icons/fa'
import { Packer } from 'docx';
import axios from 'axios';
import DocEditor from '../DocEditor'
import { fetchRefreshToken } from '../../../../../services/api/user'
import SOPModal from '../SOPModal'
import { config, general } from '../../../../../constants'
import { useTranslation } from 'react-i18next'
import { getUserPermissions } from '../utils/getPermission'
import { createSOWDocument } from '../GenerateSOWTemplate';

const { Search } = Input

const DocumentHeader = ({
  orgUserList,
  docEditorFile,
  userId,
  userName,
  userOrg,
  tab,
  defaultQuery,
  isSuperAdmin,
  isAdmin,
  query,
  handleChangeSearch,
  openUploadModal,
  setIsCreateFolderModalVisible,
  user,
  shlToken,
  setIsSOPModalVisible,
  documentData,
  handleDocTypeModalVisible,
  isSOPModalVisible,
  resetDocumentData,
  upload
}) => {
  console.log("docEditorFile log", docEditorFile)
  const isDHL = user?.info?.organization?.enable_custom_sop || false;
  const [visible, setVisible] = useState(false)
  const [refreshToken, setRefreshToken] = useState(null);
  const [isCreatingFile, setIsCreatingFile] = useState(false)
  const [creatingFileType, setCreatingFileType] = useState('')
  const [showDocEditor, setShowDocEditor] = useState(false)
  const [editorState, setEditorState] = useState({
    editorParams: {
      documentName: '',
      documentId: '',
      userId: '',
      userName: ''
    },
    isShowEditor: false
  })
  const [docPath, setDocPath] = useState('')
  const [docName, setDocName] = useState('')
  const [selectedDocType, setSelectedDocType] = useState(null)
  const [sopData, setSopData] = useState(null); // store API response data

  useEffect(() => {
    if (sopData && sopData.data) {
      console.log("sopData updated, opening modal:", sopData);
      setIsSOPModalVisible(true);
      resetDocumentData();
    }
  }, [sopData]);

  useEffect(() => {
    console.log("editorState updated:", editorState)
  }, [editorState])

  useEffect(() => {
    const fetchTokens = async () => {
      const refresh = await fetchRefreshToken();
      setRefreshToken(refresh);
    };

    fetchTokens();
  }, []);

  useEffect(() => {
    const createDocument = async () => {
      // 1️⃣ Only run if user clicked "Create"
      if (!documentData || !documentData.isCreate) return;

      // 2️⃣ Validate type and name
      if (!documentData.type) {
        console.warn("⛔ Document type missing");
        return;
      }
      if (!documentData.name || !documentData.name.trim()) {
        console.warn("⛔ Document name missing");
        return;
      }

      // 3️⃣ Check upload function
      if (!upload) {
        console.error('Upload function not provided')
        return
      }

      // 4️⃣ Close the modal since user has selected a document type
      handleDocTypeModalVisible(false)

      let fileConfig = null

      // 5️⃣ Determine file configuration based on document type
      switch (selectedDocType) {
        case 'writer':
          setCreatingFileType('Writer Document')

          if (documentData.type === 'global_sop_keystroke') {
            console.warn('global_sop_keystroke template not yet implemented')
            message.warning('This document type is not yet implemented')
            setIsCreatingFile(false)
            setCreatingFileType('')
            resetDocumentData()
            return

          } else if (documentData.type === 'bp_specific_sop_keystroke') {
            console.warn('bp_specific_sop_keystroke template not yet implemented')
            message.warning('This document type is not yet implemented')
            setIsCreatingFile(false)
            setCreatingFileType('')
            resetDocumentData()
            return

          } else if (documentData.type === 'global_statement_of_work') {
            try {
              const doc = await createSOWDocument({
                title: `GSC-Business Support-Reporting and business enabling\nservices-Configuration of customized reports-SOW-V1.0`,
                version: "V1.0",
                signedOffDate: new Date().toLocaleDateString('en-GB'),
                signedBy: documentData.signedBy || "",
                role: documentData.role || ""
              })

              const blob = await Packer.toBlob(doc)

              fileConfig = {
                content: blob,
                type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                extension: 'docx',
                prefix: 'SOW',
                loadingMessage: 'Creating Global Statement of Work...',
                successMessage: 'Global Statement of Work created and uploaded successfully!',
                errorMessage: 'Error creating Global Statement of Work'
              }
            } catch (error) {
              console.error('Error generating SOW document:', error)
              message.error('Failed to generate Statement of Work template')
              setIsCreatingFile(false)
              setCreatingFileType('')
              resetDocumentData()
              return
            }

          } else if (documentData.type === 'appendix_document') {
            console.warn('appendix_document template not yet implemented')
            message.warning('This document type is not yet implemented')
            setIsCreatingFile(false)
            setCreatingFileType('')
            resetDocumentData()
            return

          } else if (documentData.type === 'general_document') {
            fileConfig = {
              content: '',
              type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
              extension: 'docx',
              prefix: 'document',
              loadingMessage: 'Creating Writer document...',
              successMessage: 'Writer document created and uploaded successfully!',
              errorMessage: 'Error creating Writer document'
            }

          } else {
            console.error('Unknown writer document subtype:', documentData.type)
            message.error(`Unknown document type: ${documentData.type}`)
            resetDocumentData()
            return
          }
          break

        case 'calc':
          setCreatingFileType('Calc Spreadsheet')
          fileConfig = {
            content: '',
            type: 'application/vnd.ms-excel',
            extension: 'xls',
            prefix: 'spreadsheet',
            loadingMessage: 'Creating Calc spreadsheet...',
            successMessage: 'Calc spreadsheet created and uploaded successfully!',
            errorMessage: 'Error creating Calc spreadsheet'
          }
          break

        case 'impress':
          setCreatingFileType('Impress Presentation')
          fileConfig = {
            content: '',
            type: 'application/vnd.ms-powerpoint',
            extension: 'ppt',
            prefix: 'presentation',
            loadingMessage: 'Creating Impress presentation...',
            successMessage: 'Impress presentation created and uploaded successfully!',
            errorMessage: 'Error creating Impress presentation'
          }
          break

        default:
          console.error('Unknown document type:', selectedDocType)
          return
      }

      // Skip if fileConfig is still null
      if (!fileConfig) {
        console.error('File configuration is null')
        return
      }

      setIsCreatingFile(true)
      message.loading(fileConfig.loadingMessage, 0)

      try {
        // Create blob and file - handle both Blob and string content
        const blob = fileConfig.content instanceof Blob
          ? fileConfig.content
          : new Blob([fileConfig.content], { type: fileConfig.type })

        const filename = `${fileConfig.prefix}_${Date.now()}.${fileConfig.extension}`
        const file = new File([blob], filename, { type: fileConfig.type })

        // Upload the file
        const response = await upload(file, { parent_id: query.parent_id }, general.ORGANIZATION_LIBRARY)

        message.destroy()
        message.success(fileConfig.successMessage)

        // Update document type via API after creating the file
        if (response?.id) {
          const documentTypeMap = isDHL
            ? {
              'global_statement_of_work': 3,
              'annexure_document': 8,
              'general_document': 5,
            }
            : {
              'global_sop_keystroke': 1,
              'bp_specific_sop_keystroke': 2,
              'global_statement_of_work': 3,
              'appendix_document': 4,
              'general_document': 5,
            }

          const document_type_id = documentTypeMap[documentData.type]

          if (document_type_id) {
            try {
              const apiResponse = await axios.put(
                `${config.aiContentGenerationUrl}sop-management/media/${response.id}/document-type?org_id=${response.organization_id}`,
                { document_type_id, title: documentData.name },
                { headers: { Authorization: `Bearer ${shlToken}`, "Content-Type": "application/json" } }
              )
              console.log("✅ Updated document type successfully:", apiResponse.data)
              setSopData(apiResponse?.data || apiResponse)
            } catch (error) {
              console.error("❌ Document type update failed:", error.response?.data || error.message)
              message.warning('Document created but failed to update document type')
            }
          } else {
            console.error('Unknown document type mapping for:', documentData.type)
          }
        }

        // Show DocEditor after successful upload
        if (response?.path) {
          setDocPath(response.path)
          if (response.title) setDocName(response.title)
        }
      } catch (error) {
        message.destroy()
        message.error(fileConfig.errorMessage)
        console.error(`Error creating/uploading ${documentData.name} file:`, error)
      } finally {
        setIsCreatingFile(false)
        setCreatingFileType('')
        resetDocumentData()
      }
    }

    createDocument()
  }, [documentData])

  const { t } = useTranslation()

  const handleMenuClick = key => {
    setVisible(false)
    switch (key) {
      case 'file-upload':
        openUploadModal({ parent_id: query.parent_id, privacy_type: '' })
        break
      case 'new-folder':
        setIsCreateFolderModalVisible(true)
        break
      case 'writer':
        // Create and upload empty .docx file
        handleDocxGeneration()
        break
      case 'calc':
        // Create and upload empty Excel file
        handleExcelGeneration()
        break
      case 'impress':
        // Create and upload empty PowerPoint file
        handlePowerPointGeneration()
        break
      case 'screen_recording':
        // Create and upload empty PowerPoint file
        handleScreenRecording()
        break
      default:
        break
    }
  }

  const handleDocxGeneration = () => {
    setSelectedDocType('writer')
    handleDocTypeModalVisible(true)
  }

  const handleExcelGeneration = () => {
    setSelectedDocType('calc')
    handleDocTypeModalVisible(true)
  }

  const handlePowerPointGeneration = () => {
    setSelectedDocType('impress')
    handleDocTypeModalVisible(true)
  }

  const handleScreenRecording_ = async () => {
    const base_Url = config.baseURL.replace(/\/v1\/?$/, '')
    const ai_Url = config.questionAnswerAiUrl
    // const deepLink = `sharelookapp://open?token=${shlToken}&base_Url=${base_Url}&ai_Url=${ai_Url}&userId=${userId}&course_Id=7488&template_id=7`;
    const deepLink = `sharelookapp://open?token=${shlToken}&refreshToken=${refreshToken}&base_Url=${base_Url}&ai_Url=${ai_Url}&userId=${userId}&template_id=7`
    try {
      // Method 1: Try direct navigation
      window.location.href = deepLink

      // Method 2: Fallback using a temporary <a> element
      const link = document.createElement('a')
      link.href = deepLink
      link.style.display = 'none'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (error) {
      console.error('Deep link failed:', error)
    }
  }

  const handleScreenRecording = async () => {
    const base_Url = config.baseURL.replace(/\/v1\/?$/, '')
    const ai_Url = config.questionAnswerAiUrl
    console.log('refreshToken log', refreshToken)

    console.log("shlToken:", shlToken);
    console.log("userOrg:", userOrg);
    console.log("refreshToken:", refreshToken);
    console.log("base_Url:", base_Url);
    console.log("ai_Url:", ai_Url);
    console.log("userId:", userId);

    const deepLink = `sharelookapp://open?token=${shlToken}&org_id=${userOrg}&refreshToken=${refreshToken}&base_Url=${base_Url}&ai_Url=${ai_Url}&userId=${userId}&template_id=7`;

    console.log("Generated Deep Link:", deepLink);


    // Show confirmation modal first (provides user gesture)
    showLaunchConfirmation(deepLink)
  }

  // Show confirmation modal using Ant Design
  const showLaunchConfirmation = deepLink => {
    const modal = Modal.confirm({
      title: (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <FaDesktop style={{ marginRight: '8px', color: '#1890ff' }} />
          {t('v4:launch_sharelook_application')}
        </div>
      ),
      content: (
        <div>
          <p style={{ marginBottom: '16px' }}>
            {t(
              'v4:this_will_open_the_sharelook_application_for_screen_capture'
            )}
          </p>
          <p style={{ color: '#666', fontSize: '14px', marginBottom: '0' }}>
            {t('v4:sharelook_dosenot_open')}
          </p>
        </div>
      ),
      okText: (
        <span>
          <FaDesktop style={{ marginRight: '6px' }} />
          {t('v4:open_sharelook')}
        </span>
      ),
      cancelText: t('v4:cancel'),
      okType: 'primary',
      width: 450,
      onOk: () => {
        attemptAppLaunch(deepLink)
      },
      onCancel: () => {
        console.log('User cancelled app launch')
      },
      footer: (_, { OkBtn, CancelBtn }) => (
        <div style={{ textAlign: 'center' }}>
          <CancelBtn />
          <OkBtn />
          <Button
            type="default"
            onClick={() => {
              window.open(
                'https://mj-scorm-bucket.s3.us-east-1.amazonaws.com/windows-screen-capture-app/ELearningSetup.exe',
                '_blank'
              )
              modal.destroy()
            }}
            style={{ marginLeft: '8px' }}
          >
            <FaDownload style={{ marginRight: '6px' }} />
            {t('v4:download_app')}
          </Button>
        </div>
      )
    })
  }

  // Attempt to launch the app with proper user gesture
  const attemptAppLaunch = deepLink => {
    console.log('Attempting to launch ShareLook with deepLink:', deepLink)

    // Show loading message
    const hide = message.loading(t('v5:opening_sharelook'), 2)

    // Track if app launched successfully
    let appLaunched = false

    const detectAppLaunch = () => {
      const onBlur = () => {
        appLaunched = true
        cleanup()
        hide()
        message.success(t('v5:shareLook_opened_successfully'))
      }

      const onVisibilityChange = () => {
        if (document.hidden) {
          appLaunched = true
          cleanup()
          hide()
          message.success(t('v5:shareLook_opened_successfully'))
        }
      }

      const cleanup = () => {
        window.removeEventListener('blur', onBlur)
        document.removeEventListener('visibilitychange', onVisibilityChange)
      }

      // Listen for app launch indicators
      window.addEventListener('blur', onBlur)
      document.addEventListener('visibilitychange', onVisibilityChange)

      // Check after delay if app didn't launch
      setTimeout(() => {
        cleanup()
        hide()
        if (!appLaunched) {
          showAppNotFound()
        }
      }, 3000)
    }

    try {
      // Method 1: Direct window location (most reliable with user gesture)
      detectAppLaunch()
      window.location.href = deepLink
    } catch (error) {
      console.error('Direct launch failed, trying fallback:', error)

      try {
        // Method 2: Create and click link (fallback)
        const link = document.createElement('a')
        link.href = deepLink
        link.target = '_self'
        link.style.display = 'none'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } catch (fallbackError) {
        console.error('All launch methods failed:', fallbackError)
        hide()
        showAppNotFound()
      }
    }
  }

  // Show message when app launch fails
  const showAppNotFound = () => {
    Modal.error({
      title: (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <FaExclamationTriangle
            style={{ marginRight: '8px', color: '#ff4d4f' }}
          />
          {t('v4:sharelook_not_found')}
        </div>
      ),
      content: (
        <div>
          <p style={{ marginBottom: '16px', lineHeight: '1.5' }}>
            {t('v4:sharelook_screen_capture_application_not_be_opened')}
          </p>
          <div style={{ textAlign: 'center', marginTop: '20px' }}>
            <Button
              type="primary"
              size="large"
              onClick={() => {
                window.open(
                  'https://mj-scorm-bucket.s3.us-east-1.amazonaws.com/windows-screen-capture-app/ELearningSetup.exe',
                  '_blank'
                )
                Modal.destroyAll()
              }}
            >
              <FaDownload style={{ marginRight: '6px' }} />
              {t('v4:download_app')}
            </Button>
          </div>
        </div>
      ),
      okText: t('v4:close'),
      width: 400,
      centered: true
    })
  }

  // Method to check app installation (optional enhancement)
  const checkAppInstallation = () => {
    // This is a simple check - can be enhanced based on your needs
    const isWindows = navigator.platform.indexOf('Win') > -1

    if (!isWindows) {
      message.warning('ShareLook is currently only available for Windows.')
      return false
    }

    return true
  }

  // Enhanced handler with installation check
  const handleScreenRecordingWithCheck = async () => {
    // Check if running on supported platform
    if (!checkAppInstallation()) {
      return
    }

    // Proceed with normal flow
    await handleScreenRecording()
  }

  const handleSetShowEditor = (docFile) => {
    console.log("docFile log", docFile)
    setEditorState({
      editorParams: {
        documentName: docFile?.editorParams?.documentName,  // Access nested property
        documentId: docFile?.editorParams?.documentId,
        userId: docFile?.editorParams?.userId,
        userName: docFile?.editorParams?.userName
      },
      isShowEditor: true
    })
  }

  const menu = (
    <div className="file-menu-dropdown">
      <div className="file-menu-top">
        <div
          className="file-menu-action"
          onClick={() => handleMenuClick('new-folder')}
        >
          <FolderAddOutlined className="menu-icon" />
          <span className="menu-text">{t('v4:new_folder')}</span>
        </div>
        <div
          className="file-menu-action"
          onClick={() => handleMenuClick('file-upload')}
        >
          <FileAddOutlined className="menu-icon" />
          <span className="menu-text">{t('v4:file_upload')}</span>
        </div>
      </div>
      {((isAdmin || isSuperAdmin) && tab && tab === general.ORGANIZATION_LIBRARY) && (
        <div className="file-menu-bottom">
          <div
            className={`file-menu-app ${isCreatingFile && creatingFileType === 'Writer Document'
              ? 'creating'
              : ''
              }`}
            onClick={() => !isCreatingFile && handleMenuClick('writer')}
          >
            <FaFileWord className="file-menu-app-icon" color="#2b579a" />
            <div className="file-menu-app-labels">
              <span className="file-menu-app-title">{t('v4:writer')}</span>
              <span className="file-menu-app-desc">
                {t('v4:text_documents')}
              </span>
            </div>
            <RightOutlined className="file-menu-app-arrow" />
          </div>
          <div
            className={`file-menu-app ${isCreatingFile && creatingFileType === 'Calc Spreadsheet'
              ? 'creating'
              : ''
              }`}
            onClick={() => !isCreatingFile && handleMenuClick('calc')}
          >
            <FaFileExcel className="file-menu-app-icon" color="#217346" />
            <div className="file-menu-app-labels">
              <span className="file-menu-app-title">Calc</span>
              <span className="file-menu-app-desc">{t('v4:spreadsheets')}</span>
            </div>
            <RightOutlined className="file-menu-app-arrow" />
          </div>
          <div
            className={`file-menu-app ${isCreatingFile && creatingFileType === 'Impress Presentation'
              ? 'creating'
              : ''
              }`}
            onClick={() => !isCreatingFile && handleMenuClick('impress')}
          >
            <FaFilePowerpoint className="file-menu-app-icon" color="#d24726" />
            <div className="file-menu-app-labels">
              <span className="file-menu-app-title">{t('v4:impress')}</span>
              <span className="file-menu-app-desc">
                {t('v4:presentations')}
              </span>
            </div>
            <RightOutlined className="file-menu-app-arrow" />
          </div>
          <div
            className={`file-menu-app ${isCreatingFile && creatingFileType === 'Screen Recording'
              ? 'opening desktop app'
              : ''
              }`}
            onClick={() =>
              !isCreatingFile && handleMenuClick('screen_recording')
            }
          >
            <FaDesktop className="file-menu-app-icon" color="#d24726" />
            <div className="file-menu-app-labels">
              <span className="file-menu-app-title">
                {t('v4:capture_desktop_screen')}
              </span>
              <span className="file-menu-app-desc">
                {t('v4:capture_desktop_screens')}
              </span>
            </div>
            <RightOutlined className="file-menu-app-arrow" />
          </div>
        </div>
      )}
    </div>
  )

  return (
    <div className="document-header">
      <Modal
        visible={editorState?.isShowEditor}
        onCancel={() => setEditorState(prev => ({ ...prev, isShowEditor: false }))}
        footer={null}
        width="90%"
        bodyStyle={{ height: '80vh', padding: 0 }}
        destroyOnClose={true}
        maskClosable={false}
      >
        <DocEditor
          orgUserList={orgUserList}
          isDocOpened={editorState?.isShowEditor}
          documentName={editorState?.editorParams?.documentName || docEditorFile?.title || docName}
          documentId={editorState?.editorParams?.documentId || docEditorFile?.path || docPath}
          userId={editorState?.editorParams?.userId || docEditorFile?.creator?.id || userId}
          userName={editorState?.editorParams?.userName || docEditorFile?.creator?.username || userName}
          closeModal={() => setEditorState(prev => ({ ...prev, isShowEditor: false }))}
          shlToken={shlToken}
          {...getUserPermissions(user, orgUserList, docEditorFile, true)}
          //isEdit
          //isView,
          orgId={userOrg}
        />
      </Modal>
      {((docEditorFile && Object.keys(docEditorFile).length > 0) || (isSOPModalVisible && sopData && Object.keys(sopData).length > 0)) ? (
        <Modal
          visible={
            (docEditorFile && Object.keys(docEditorFile).length > 0) ||
            (isSOPModalVisible && sopData && Object.keys(sopData).length > 0)
          }
          onCancel={() => {
            setIsSOPModalVisible(false);
            // Add logic to clear docEditorFile if needed
            // setDocEditorFile(null);
          }}
          footer={null}
          width="100%"
          style={{ maxWidth: '1500px', top: 20 }}
          bodyStyle={{ minHeight: '60vh', padding: '24px' }}
          destroyOnClose
        >
          <SOPModal
            docEditorFile={docEditorFile}
            data={sopData}
            userId={userId}
            userName={userName}
            orgId={userOrg}
            shlToken={shlToken}
            refreshToken={refreshToken}
            setEditor={handleSetShowEditor}
            isDHL
          />
        </Modal>
      ) : (
        <>
          <div className="header-left">
            {defaultQuery.isActiveOrg && (
              <>
                <div className="header-left-title">{t('v4:your_documents')}</div>
                <div className="header-left-description">
                  {t('v4:manage_your_videos_images_audio_files')}
                </div>
              </>
            )}

            {defaultQuery.isOrg && (
              <>
                <div className="header-left-title">
                  {t('v4:your_organization_documents')}
                </div>
                <div className="header-left-description">
                  {t('v4:keep_your_organization_creative_assets_organized')}
                </div>
              </>
            )}

            {defaultQuery.isPublic && (
              <>
                <div className="header-left-title">
                  {t('v4:your_public_documents')}
                </div>
                <div className="header-left-description">
                  {t('v4:curated_collection_of_media_assets_available')}
                </div>
              </>
            )}

            {defaultQuery.isDownload && (
              <>
                <div className="header-left-title">
                  {t('v4:your_download_center_documents')}
                </div>
                <div className="header-left-description">
                  {t('v4:repository_of_approved_media_assets_for_download')}
                </div>
              </>
            )}
          </div>

          <div className="header-right">
            <Search
              placeholder={t('v4:search_files_and_folders')}
              defaultValue={query.search}
              onChange={handleChangeSearch}
              className="header-right-search"
              allowClear
            />

            {!defaultQuery.isDownload &&
              ((tab === 'My Media' && (!isAdmin || !isSuperAdmin)) ||
                (tab !== 'Public Media' && (isAdmin || isSuperAdmin))) && (
                <div className="header-actions">
                  <Dropdown
                    overlay={menu}
                    trigger={['click']}
                    visible={visible}
                    onVisibleChange={setVisible}
                    placement="bottomRight"
                    overlayClassName="custom-dropdown"
                  >
                    <Button type="primary" icon="plus" loading={isCreatingFile}>
                      {isCreatingFile
                        ? `Creating ${creatingFileType}...`
                        : t('v4:new')}
                    </Button>
                  </Dropdown>
                </div>
              )}
          </div>
        </>
      )}
    </div>
  )
}

export default DocumentHeader